import { useState } from 'react';
import { BoutonPrincipal, MessageErreur } from '../ui/Primitives';
import type { QuestionnaireARemplir, ReponsesQuestionnaire } from '../../lib/useQuestionnaires';

const NOTES = [1, 2, 3, 4, 5];
const NOTES_NPS = Array.from({ length: 11 }, (_, note) => note);

/**
 * Questionnaire à remplir, le même dans l'espace apprenant et derrière le lien
 * personnel. Une question peut rester sans réponse ; une note se donne d'un
 * geste, de 1 (pas du tout satisfait) à 5 (très satisfait), et la
 * recommandation de 0 (pas du tout probable) à 10 (très probable) — celle du
 * NPS (TF-434), sur deux lignes en mobile.
 */
export function FormulaireQuestionnaire({
  questionnaire,
  enCours,
  erreur,
  onEnvoyer,
}: {
  questionnaire: QuestionnaireARemplir;
  enCours: boolean;
  erreur: string | null;
  onEnvoyer: (reponses: ReponsesQuestionnaire) => void;
}): JSX.Element {
  const [reponses, setReponses] = useState<ReponsesQuestionnaire>({});
  const repondues = Object.values(reponses).filter(
    (reponse) => reponse.note !== undefined || (reponse.choix ?? []).length > 0 || (reponse.texte ?? '').trim(),
  ).length;

  const majChoix = (questionId: string, choixId: string, unique: boolean): void =>
    setReponses((courantes) => {
      const deja = courantes[questionId]?.choix ?? [];
      const choix = unique ? [choixId] : deja.includes(choixId) ? deja.filter((id) => id !== choixId) : [...deja, choixId];
      return { ...courantes, [questionId]: { choix } };
    });

  return (
    <div className="space-y-5">
      {questionnaire.introduction && (
        <p className="rounded-xl bg-codialis-sand px-4 py-3 text-sm text-codialis-navy">{questionnaire.introduction}</p>
      )}

      <ol className="space-y-4">
        {questionnaire.questions.map((question, rang) => (
          <li key={question.id} className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
            <fieldset className="min-w-0 space-y-3">
              <legend className="w-full text-sm font-semibold text-codialis-navy">
                <span className="mr-2 text-codialis-gray">{rang + 1}.</span>
                {question.libelle}
              </legend>

              {question.type === 'NOTE' && (
                <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={question.libelle}>
                  {NOTES.map((note) => {
                    const choisie = reponses[question.id]?.note === note;
                    return (
                      <button
                        key={note}
                        type="button"
                        role="radio"
                        aria-checked={choisie}
                        aria-label={`${note} sur 5`}
                        onClick={() => setReponses((courantes) => ({ ...courantes, [question.id]: { note } }))}
                        disabled={enCours}
                        className={`h-12 w-12 rounded-xl border text-sm font-bold transition ${
                          choisie
                            ? 'border-codialis-navy bg-codialis-navy text-white'
                            : 'border-gray-200 bg-white text-codialis-navy hover:bg-gray-50'
                        }`}
                      >
                        {note}
                      </button>
                    );
                  })}
                </div>
              )}

              {question.type === 'NPS' && (
                <div className="space-y-2">
                  <div
                    className="grid grid-cols-6 gap-2 sm:grid-cols-11 sm:gap-1.5"
                    role="radiogroup"
                    aria-label={question.libelle}
                  >
                    {NOTES_NPS.map((note) => {
                      const choisie = reponses[question.id]?.note === note;
                      return (
                        <button
                          key={note}
                          type="button"
                          role="radio"
                          aria-checked={choisie}
                          aria-label={`${note} sur 10`}
                          onClick={() => setReponses((courantes) => ({ ...courantes, [question.id]: { note } }))}
                          disabled={enCours}
                          className={`h-12 min-w-0 rounded-xl border text-sm font-bold transition ${
                            choisie
                              ? 'border-codialis-navy bg-codialis-navy text-white'
                              : 'border-gray-200 bg-white text-codialis-navy hover:bg-gray-50'
                          }`}
                        >
                          {note}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex justify-between gap-3 text-[11px] text-codialis-gray">
                    <span>0 · pas du tout probable</span>
                    <span className="text-right">10 · très probable</span>
                  </div>
                </div>
              )}

              {(question.type === 'CHOIX_UNIQUE' || question.type === 'CHOIX_MULTIPLE') && (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {question.choix.map((choix) => {
                    const unique = question.type === 'CHOIX_UNIQUE';
                    const coche = (reponses[question.id]?.choix ?? []).includes(choix.id);
                    return (
                      <label
                        key={choix.id}
                        className={`flex min-h-[48px] min-w-0 cursor-pointer items-center gap-3 rounded-xl border px-3 py-2 text-sm transition ${
                          coche ? 'border-codialis-navy bg-codialis-sand' : 'border-gray-200 bg-white hover:bg-gray-50'
                        } text-codialis-navy`}
                      >
                        <input
                          type={unique ? 'radio' : 'checkbox'}
                          name={question.id}
                          checked={coche}
                          disabled={enCours}
                          onChange={() => majChoix(question.id, choix.id, unique)}
                          className="h-5 w-5 shrink-0"
                        />
                        <span className="min-w-0 break-words">{choix.libelle}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              {question.type === 'TEXTE' && (
                <textarea
                  value={reponses[question.id]?.texte ?? ''}
                  onChange={(e) =>
                    setReponses((courantes) => ({ ...courantes, [question.id]: { texte: e.target.value } }))
                  }
                  rows={3}
                  maxLength={2000}
                  disabled={enCours}
                  aria-label={question.libelle}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20"
                />
              )}
            </fieldset>
          </li>
        ))}
      </ol>

      {erreur && <MessageErreur message={erreur} />}

      <BoutonPrincipal
        onClick={() =>
          onEnvoyer(
            Object.fromEntries(
              Object.entries(reponses).filter(
                ([, reponse]) => reponse.note !== undefined || (reponse.choix ?? []).length > 0 || (reponse.texte ?? '').trim(),
              ),
            ),
          )
        }
        disabled={enCours || repondues === 0}
      >
        {enCours ? 'Envoi…' : 'Envoyer mes réponses'}
      </BoutonPrincipal>
    </div>
  );
}
